<template>
  <div class="absolute inset-0 pointer-events-none overflow-hidden">
    <!-- 浮动装饰元素 -->
    <div 
      v-for="(element, index) in elements" 
      :key="index"
      :class="[
        'absolute rounded-full',
        element.class,
        element.animation
      ]"
      :style="element.style"
    ></div>
    
    <!-- 光环效果 -->
    <div 
      v-if="showBorder && borderConfig"
      :class="[
        'absolute inset-0 border pointer-events-none',
        borderConfig.class,
        borderConfig.animation
      ]"
      :style="borderConfig.style"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface ElementConfig {
  class: string;
  style: string;
  animation: string;
}

interface Props {
  variant?: 'hero-image' | 'card' | 'minimal' | 'custom';
  showBorder?: boolean;
  borderClass?: string;
  borderStyle?: string;
  customElements?: ElementConfig[];
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'card',
  showBorder: false,
  borderClass: 'border-primary/20 animate-pulse-border rounded-lg',
  borderStyle: '',
  customElements: undefined
});

const elements = computed(() => {
  if (props.customElements) {
    return props.customElements;
  }

  switch (props.variant) {
    case 'hero-image':
      return [
        {
          class: 'w-8 h-8 bg-gradient-to-br from-primary/60 to-primary/30 opacity-80',
          style: 'top: -1rem; left: -1rem;',
          animation: 'animate-float-gentle'
        },
        {
          class: 'w-6 h-6 bg-gradient-to-tl from-primary/50 to-primary/20 opacity-70',
          style: 'bottom: -0.5rem; right: -1.5rem;',
          animation: 'animate-float-reverse'
        }
      ];
    case 'card':
      return [
        {
          class: 'w-4 h-4 bg-primary/30',
          style: 'top: 1rem; right: 1rem;',
          animation: 'animate-pulse-gentle'
        },
        {
          class: 'w-3 h-3 bg-primary/20',
          style: 'bottom: 1rem; left: 1rem;',
          animation: 'animate-float-gentle'
        }
      ];
    case 'minimal':
      return [
        {
          class: 'w-2 h-2 bg-primary/40',
          style: 'top: 0.5rem; right: 0.5rem;',
          animation: 'animate-ping'
        }
      ];
    default:
      return [];
  }
});

const borderConfig = computed(() => {
  if (!props.showBorder) return null;
  
  return {
    class: props.borderClass,
    style: props.borderStyle,
    animation: ''
  };
});
</script>

<style scoped>
@keyframes floatGentle {
  0%, 100% {
    transform: translateY(0px) scale(1);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-8px) scale(1.1);
    opacity: 1;
  }
}

@keyframes floatReverse {
  0%, 100% {
    transform: translateY(0px) translateX(0px) scale(1);
  }
  33% {
    transform: translateY(15px) translateX(-10px) scale(1.1);
  }
  66% {
    transform: translateY(-25px) translateX(8px) scale(0.9);
  }
}

@keyframes pulseGentle {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.2);
  }
}

@keyframes pulseBorder {
  0%, 100% {
    border-color: hsl(var(--primary) / 0.2);
    box-shadow: 0 0 0 0 hsl(var(--primary) / 0.3);
  }
  50% {
    border-color: hsl(var(--primary) / 0.5);
    box-shadow: 0 0 0 4px hsl(var(--primary) / 0.1);
  }
}

.animate-float-gentle {
  animation: floatGentle 3s ease-in-out infinite;
}

.animate-float-reverse {
  animation: floatReverse 6s ease-in-out infinite;
  animation-delay: -2s;
}

.animate-pulse-gentle {
  animation: pulseGentle 2s ease-in-out infinite;
}

.animate-pulse-border {
  animation: pulseBorder 3s ease-in-out infinite;
}
</style>
